import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/shop';
import { usePageTitle } from '../a11y/usePageTitle';

/**
 * Opting out of promotional email, from the link at the bottom of one.
 *
 * NO SIGN-IN. CAN-SPAM requires that opting out takes no more than a visit to a
 * single page; the token in the link is the credential, and it can only switch the
 * named person's promotions off.
 *
 * A BUTTON, NOT AN AUTOMATIC UNSUBSCRIBE ON LOAD. Mail scanners and link previews
 * open every link in a message; a page that unsubscribed on load would opt people
 * out of mail they wanted, without them ever seeing it. Receipts, voucher reminders
 * and anything about money on their account are unaffected - the page says so,
 * because "will I still get my vouchers?" is the next question.
 */
export function Unsubscribe() {
  usePageTitle('Unsubscribe');

  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const [state, setState] = useState<'ready' | 'working' | 'done' | 'invalid'>(
    token === '' ? 'invalid' : 'ready',
  );

  async function confirm() {
    setState('working');

    try {
      await api.unsubscribe(token);
      setState('done');
    } catch {
      setState('invalid');
    }
  }

  return (
    <section className="section">
      <div className="panel stack" style={{ gap: 14, maxWidth: 560 }}>
        {state === 'done' && (
          <>
            <h1>You are unsubscribed</h1>
            <p role="status">
              We will not send you offers or announcements by email any more.
            </p>
            <p className="muted">
              You will still get receipts, voucher reminders and anything about money on your
              account. You can change this any time in your account settings.
            </p>
            <Link to="/" className="btn secondary">Back to the shop</Link>
          </>
        )}

        {state === 'invalid' && (
          <>
            <h1>This link did not work</h1>
            <p className="muted">
              It may have been cut short by your email app. Sign in and switch promotions off in
              your account settings instead - it takes a moment.
            </p>
            <Link to="/account" className="btn secondary">Go to my account</Link>
          </>
        )}

        {(state === 'ready' || state === 'working') && (
          <>
            <h1>Stop promotional emails?</h1>
            <p className="muted">
              You will stop getting offers and announcements by email. Receipts, voucher reminders
              and anything about money on your account will still arrive.
            </p>
            <div>
              <button
                type="button"
                className="btn"
                disabled={state === 'working'}
                onClick={() => void confirm()}
              >
                {state === 'working' ? 'Unsubscribing…' : 'Unsubscribe'}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
